import React from 'react';
import { useParams } from 'react-router-dom';
import { CalendarIcon, MapPinIcon } from 'lucide-react';
import { usePageMeta } from '../../hooks/usePageMeta';
import { DestinationHero } from '../../components/destination/DestinationHero';
import { Section } from '../../components/ui/Section';
import { ActionLink } from '../../components/ui/ActionLink';
import { TextLink } from '../../components/ui/TextLink';
import { CourseCard } from '../../components/ui/CourseCard';
import { MockupTag } from '../../components/ui/MockupTag';
import { BenefitList } from '../../components/ui/BenefitList';
import { FaqList } from '../../components/ui/FaqList';
import { EnquirySection } from '../../components/forms/EnquirySection';
import { NotFound } from '../NotFound';
import { destinations } from '../../data/destinations';
import { courses } from '../../data/courses';
import { activityWeek, seasonalCare } from '../../data/pageContent';
import { contact } from '../../data/contact';
export function SeasonalDestination() {
  const {
    slug
  } = useParams();
  const destination = destinations.find(item => item.slug === slug);
  const copy = destination?.seasonal;
  usePageMeta(copy?.metaTitle ?? 'Location not found | ES World', copy?.metaDescription ?? 'This page does not exist.');
  if (!destination || !copy) return <NotFound data-id="element-1101" data-mp="2owa2m0t53fl" />;
  const available = courses.filter(course => course.season === 'seasonal' && course.locations.includes(destination.slug));
  return <>
      <DestinationHero destination={destination} season="seasonal" headline={copy.h1} lead={copy.lead} primary={{
      label: 'Ask about seasonal dates',
      to: '#enquire'
    }} secondary={{
      label: 'See programmes',
      to: '#programmes'
    }} stats={[{
      label: destination.campuses.length > 1 ? 'Campuses' : 'Campus',
      value: String(destination.campuses.length)
    }, {
      label: 'Programmes',
      value: String(available.length)
    }, {
      label: 'Sessions',
      value: String(copy.dates.length)
    }, {
      label: 'Open',
      value: 'Summer & winter'
    }]} data-id="element-1102" data-mp="vca000638yq7" />

      <Section id="programmes" title="Programmes this season" description={`These summer and winter programmes run in ${destination.name}. Each one mixes lessons with activities and trips.`} data-id="element-1103" data-mp="17mhst1uvqbmq">
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4" data-id="element-1104" data-mp="xwf1d63ep5qi">
          {available.map(course => <CourseCard key={course.slug} course={course} data-id="element-1105" data-mp="two36hbjvnby" />)}
        </div>
        <div className="mt-10" data-id="element-1106" data-mp="ovgxcnsywy7x">
          <ActionLink to="/price-list/camps" data-id="element-1107" data-mp="oymkvyly7s1j">See seasonal prices</ActionLink>
        </div>
      </Section>

      <Section id="dates" bg="cream" title="Dates for this season" description="Programmes run on set dates. Book early, as places are limited for each group." mockup="Sample dates and address" aside={<TextLink to="/blog/english-summer-camp-guide-for-parents" data-id="element-1109" data-mp="29d6eg365s366">Read our guide for parents</TextLink>} data-id="element-1108" data-mp="jz05pjmk81q3">
        <div className="grid gap-10 md:grid-cols-2" data-id="element-1110" data-mp="2hscgrskupmot">
          <ul className="space-y-3" data-id="element-1111" data-mp="2lupgo0cx2qdo">
            {copy.dates.map((date, index) => <li key={`${date}-${index}`} className="flex items-center gap-3 rounded-xl border border-line bg-white px-4 py-4" data-id="element-1112" data-mp="2lsctn8saxptr" data-mpi={index}>
                <CalendarIcon className="h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1113" data-mp="2vl2543nur3p3" />
                <span className="font-semibold text-ink" data-id="element-1114" data-mp="1yurp6dqpca0i">{date}</span>
              </li>)}
          </ul>
          <div className="space-y-5" data-id="element-1115" data-mp="22v3alsqw2ed1">
            {destination.campuses.map(campus => <address key={campus.name} className="flex gap-3 not-italic" data-id="element-1116" data-mp="27uojauvv212y">
                <MapPinIcon className="mt-1 h-5 w-5 shrink-0 text-brand" aria-hidden="true" data-id="element-1117" data-mp="2cu11v3fp8lzq" />
                <span className="leading-relaxed" data-id="element-1118" data-mp="3ja3dxs0lkguw">
                  <span className="block type-h5 text-ink" data-id="element-1119" data-mp="3j9bj4jetqn5o">{campus.name}</span>
                  {campus.lines.map(line => <span key={line} className="block" data-id="element-1120" data-mp="3obamhtakdk2m">
                      {line}
                    </span>)}
                </span>
              </address>)}
            <p className="leading-relaxed" data-id="element-1121" data-mp="w3i8z33qvvn8">{destination.transport}</p>
          </div>
        </div>
      </Section>

      <Section id="week" title="A week of things to do" description="Here is a sample week of activities. The centre team shares the real plan on arrival day." mockup="Sample calendar" data-id="element-1122" data-mp="19z2f0xydkjd8">
        <ol className="divide-y divide-line border-y border-line" data-id="element-1123" data-mp="1jxi6j21bdun9">
          {activityWeek.map(row => <li key={row.day} className="grid gap-1 py-5 md:grid-cols-12 md:items-baseline md:gap-6" data-id="element-1124" data-mp="1eyp2e07x3fzr">
              <span className="type-card-title text-ink md:col-span-3" data-id="element-1125" data-mp="1str4ec9n8l7a">{row.day}</span>
              <span className="type-body md:col-span-7" data-id="element-1126" data-mp="1osfmyu0xb6a6">{row.activity}</span>
              <span className="font-semibold text-ink md:col-span-2 md:text-right" data-id="element-1127" data-mp="22qm1qru6kfjz">{row.cost}</span>
            </li>)}
        </ol>
      </Section>

      <Section id="activities" bg="grey" title="Trips with your group" description={`Students explore ${destination.name} with group leaders and new friends.`} mockup="Sample activities" data-id="element-1128" data-mp="2hd2p5404vrg8">
        <ul className="grid gap-x-10 md:grid-cols-2 lg:grid-cols-3" data-id="element-1129" data-mp="27en3vhnsfcen">
          {destination.activities.map(activity => <li key={activity.name} className="border-b border-line py-5" data-id="element-1130" data-mp="2ccx1s00wcr3y">
              <h3 className="type-card-title text-ink" data-id="element-1131" data-mp="1yml9a00egqca">{activity.name}</h3>
              <p className="mt-2 leading-relaxed" data-id="element-1132" data-mp="1yk8kp404zhq9">{activity.text}</p>
            </li>)}
        </ul>
      </Section>

      <Section id="care" title="Care from arrival to departure" subhead="Young learners are never alone. Our team looks after every student from the airport to the last day." data-id="element-1133" data-mp="1oodvfq1eqoei">
        <BenefitList items={seasonalCare} data-id="element-1134" data-mp="1tkvv4bhvrwjz" />
        <div className="mt-10 flex flex-col gap-3 sm:flex-row sm:flex-wrap" data-id="element-1135" data-mp="1fn7ddcj415j5">
          <ActionLink to="#enquire" data-id="element-1136" data-mp="1josuja2og5z5">Reserve a place for my child</ActionLink>
          <ActionLink to={contact.whatsappLink} variant="secondary" data-id="element-1137" data-mp="1jqvhfl8j9izn">
            Talk to our team on WhatsApp
          </ActionLink>
        </div>
      </Section>

      <Section id="faq" bg="cream" title="Questions from families" description="Answers to common questions before you book." data-id="element-1138" data-mp="3hk7t9wenf7zf">
        <FaqList items={[{
        q: 'Does my child need a visa?',
        a: destination.visaNote
      }, {
        q: 'Is airport transfer available?',
        a: 'Yes. Airport transfers run on set arrival and departure days. Our team confirms the details for your dates.'
      }, {
        q: 'What is included in the price?',
        a: 'Seasonal prices usually include lessons, housing, meals, activities and trips. The camps price list shows every extra fee.'
      }]} data-id="element-1139" data-mp="3cjtul4yxtae2" />
      </Section>

      <EnquirySection title="Book a place this season" description="Tell us the programme, the dates and the age of the student. Our team confirms the places available and sends a sample price." submitLabel={`Send my ${destination.name} season enquiry`} defaultLocation={destination.slug} data-id="element-1140" data-mp="3rcvl0cqrulra" />
    </>;
}